import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

const AdminFAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    id: null,
    question: '',
    answer: '',
    category: 'General'
  });

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      alert('Failed to load FAQs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.question || !formData.answer) {
      alert("Both Question and Answer are required.");
      return;
    }

    setProcessing(true);
    try {
      if (isEditing && formData.id) {
        // Update existing
        const { error } = await supabase
          .from('faqs')
          .update({
            question: formData.question,
            answer: formData.answer,
            category: formData.category
          })
          .eq('id', formData.id);
          
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('faqs')
          .insert([{
            question: formData.question,
            answer: formData.answer,
            category: formData.category
          }]);
          
        if (error) throw error;
      }
      
      resetForm();
      fetchFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      alert('Failed to save FAQ: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (faq) => {
    setFormData({
      id: faq.id,
      question: faq.question,
      answer: faq.answer,
      category: faq.category || 'General'
    });
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this FAQ?")) {
      setProcessing(true);
      try {
        const { error } = await supabase
          .from('faqs')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        fetchFaqs();
      } catch (error) {
        console.error('Error deleting FAQ:', error);
        alert('Failed to delete FAQ: ' + error.message);
      } finally {
        setProcessing(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({ id: null, question: '', answer: '', category: 'General' });
    setIsEditing(false);
  };

  return (
    <div className="p-8 min-h-screen bg-[#131313] text-white">
      <h2 className="text-3xl font-bold mb-6 text-[#f59e0b]" style={{ fontFamily: 'Bebas Neue' }}>Manage FAQs</h2>
      
      {/* Form Section */}
      <div className="bg-[#1c1b1b] p-6 rounded-xl border border-white/10 mb-8 shadow-xl">
        <h3 className="text-xl font-semibold mb-4 text-[#d8c3ad]">{isEditing ? 'Edit FAQ' : 'Add New FAQ'}</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Question</label>
            <input 
              type="text" 
              name="question"
              value={formData.question}
              onChange={handleInputChange}
              placeholder="e.g. What are your business hours?"
              className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white w-full focus:outline-none focus:border-[#f59e0b]" 
              required 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Category</label>
            <select 
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white w-full focus:outline-none focus:border-[#f59e0b]" 
              required
            >
              <option value="General">General</option>
              <option value="Services">Services</option>
              <option value="Projects">Projects</option>
              <option value="Investments">Investments</option>
              <option value="Payment">Payment</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Answer</label>
            <textarea 
              name="answer"
              value={formData.answer}
              onChange={handleInputChange}
              placeholder="Provide the detailed answer here..."
              rows={4}
              className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white w-full focus:outline-none focus:border-[#f59e0b]" 
              required 
            />
          </div>
          
          <div className="flex gap-4 mt-2">
            <button 
              type="submit" 
              disabled={processing}
              className={`flex-1 font-bold py-3 rounded transition-colors flex justify-center items-center gap-2 ${processing ? 'bg-gray-600 text-gray-300 cursor-not-allowed' : 'bg-[#f59e0b] text-[#2a1700] hover:bg-[#ffc174]'}`}
            >
              {processing ? (
                <><span className="material-symbols-outlined animate-spin text-sm">sync</span> Processing...</>
              ) : (
                isEditing ? 'Update FAQ' : 'Publish FAQ'
              )}
            </button>
            {isEditing && (
              <button 
                type="button" 
                onClick={resetForm}
                disabled={processing}
                className="px-6 py-3 bg-transparent border border-white/20 text-white rounded hover:bg-white/5 transition-colors font-bold"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* List Section */}
      <div className="bg-[#1c1b1b] rounded-xl border border-white/10 overflow-hidden shadow-xl">
        <div className="p-4 bg-white/5 border-b border-white/10 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-[#d8c3ad]">Existing FAQs</h3>
          <span className="text-xs bg-[#f59e0b]/20 text-[#f59e0b] px-3 py-1 rounded-full font-bold">{faqs.length} Total</span>
        </div>
        
        {loading ? (
          <div className="p-8 text-center text-gray-400 flex flex-col items-center">
            <span className="material-symbols-outlined animate-spin text-4xl mb-2 text-[#f59e0b]">sync</span>
            <p>Loading FAQs...</p>
          </div>
        ) : faqs.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <span className="material-symbols-outlined text-5xl mb-2 opacity-50">quiz</span>
            <p>No FAQs available. Create your first FAQ above.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {faqs.map(faq => (
              <div key={faq.id} className="p-6 hover:bg-white/5 transition-colors flex flex-col md:flex-row gap-6 justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-[#f59e0b]/20 text-[#f59e0b] text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
                      {faq.category || 'General'}
                    </span>
                    <h4 className="text-lg font-bold text-white">{faq.question}</h4>
                  </div>
                  <p className="text-gray-400 text-sm whitespace-pre-wrap">{faq.answer}</p>
                  <p className="text-xs text-gray-600 mt-4">
                    Added: {new Date(faq.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button 
                    onClick={() => handleEdit(faq)} 
                    disabled={processing}
                    className="p-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                    title="Edit FAQ"
                  >
                    <span className="material-symbols-outlined text-sm">edit</span>
                  </button>
                  <button 
                    onClick={() => handleDelete(faq.id)} 
                    disabled={processing}
                    className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                    title="Delete FAQ"
                  >
                    <span className="material-symbols-outlined text-sm">delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFAQ;
